// CSS owns automatic movement; native scrolling owns manual exploration.
if (!customElements.get("hf-marquee")) {
  customElements.define("hf-marquee", class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.viewport = this.querySelector(".hf-marquee__viewport");
      this.track = this.querySelector(".hf-marquee__track");
      this.set = this.querySelector("[data-hf-marquee-set]");
      if (!this.viewport || !this.track || !this.set) return;
      this.controller = new AbortController();
      this.logicalVideoStates = new Map();
      this.activeLogicalCards = new Map();
      this.videoScrolling = false;
      const { signal } = this.controller;
      this.track.addEventListener("animationiteration", (event) => {
        if (event.animationName === "hf-marquee-scroll") this.refreshVideoVisibility();
      }, { signal });
      this.motion = matchMedia("(prefers-reduced-motion: reduce)");
      this.finePointer = matchMedia("(hover: hover) and (pointer: fine)");
      this.visible = false;
      this.hovered = false;
      this.pointer = null;
      this.loaded = document.readyState === "complete";
      this.viewport.addEventListener("pointerdown", (event) => {
        if (!event.isPrimary || event.button !== 0) return;
        // Keep pointer dragging from moving keyboard focus to an offscreen original.
        if (event.pointerType === "mouse") event.preventDefault();
        this.suppressClick = false;
        clearTimeout(this.clickTimer);
        this.manual();
        this.pointer = { id: event.pointerId, mouse: event.pointerType === "mouse", x: event.clientX, y: event.clientY, lastX: event.clientX, dragged: false };
        this.setAttribute("data-hf-touch", "");
        this.syncVideos();
      }, { signal });
      this.viewport.addEventListener("pointermove", (event) => {
        const pointer = this.pointer;
        if (!pointer || pointer.id !== event.pointerId || !pointer.mouse) return;
        const dx = event.clientX - pointer.x;
        if (!pointer.dragged && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(event.clientY - pointer.y)) {
          pointer.dragged = true;
          this.viewport.setPointerCapture(event.pointerId);
          this.setAttribute("data-hf-dragging", "");
        }
        if (!pointer.dragged) return;
        event.preventDefault();
        this.viewport.scrollLeft -= event.clientX - pointer.lastX;
        pointer.lastX = event.clientX;
        this.normalize();
      }, { signal });
      const endPointer = (event) => {
        if (!this.pointer || this.pointer.id !== event.pointerId) return;
        this.suppressClick = this.pointer.dragged;
        this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 500);
        this.pointer = null;
        if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
        this.removeAttribute("data-hf-dragging");
        this.removeAttribute("data-hf-touch");
        this.scheduleResume();
        this.syncVideos();
      };
      this.viewport.addEventListener("pointerup", endPointer, { signal });
      this.viewport.addEventListener("pointercancel", endPointer, { signal });
      this.viewport.addEventListener("lostpointercapture", endPointer, { signal });
      this.viewport.addEventListener("pointerleave", (event) => {
        if (this.pointer && !this.pointer.dragged) endPointer(event);
      }, { signal });
      this.viewport.addEventListener("dragstart", (event) => event.preventDefault(), { signal });
      this.viewport.addEventListener("click", (event) => {
        if (!this.suppressClick || event.detail === 0) return;
        event.preventDefault();
        event.stopPropagation();
        this.suppressClick = false;
      }, { signal, capture: true });
      this.viewport.addEventListener("scroll", () => {
        if (!this.hasAttribute("data-hf-manual")) return;
        this.normalize();
        // Native momentum continues after pointerup. Resume media once it settles,
        // independently of the longer automatic-scroll resume delay.
        this.videoScrolling = true;
        this.syncVideos();
        clearTimeout(this.videoSettleTimer);
        this.videoSettleTimer = setTimeout(() => {
          this.videoScrolling = false;
          if (this.isConnected) this.syncVideos();
        }, 180);
        this.scheduleResume();
      }, { signal, passive: true });
      this.viewport.addEventListener("wheel", (event) => {
        if (Math.abs(event.deltaX) > 0 || event.shiftKey) { this.manual(); this.scheduleResume(); }
      }, { signal, passive: true });
      this.viewport.addEventListener("focusin", (event) => {
        this.manual();
        const card = event.target.closest("[data-hf-marquee-original]");
        if (card && !this.pointer) this.reveal(card);
      }, { signal });
      this.viewport.addEventListener("keydown", (event) => {
        if (event.target !== this.viewport || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
        event.preventDefault();
        this.manual();
        this.viewport.scrollBy({ left: (event.key === "ArrowRight" ? 1 : -1) * 254, behavior: "auto" });
      }, { signal });
      this.addEventListener("shopify:block:select", (event) => {
        const card = event.target.closest("[data-hf-marquee-original]");
        if (card) { this.manual(); this.reveal(card); }
      }, { signal });
      this.addEventListener("pointerenter", () => {
        this.hovered = this.finePointer.matches;
        if (this.hovered) this.manual();
        this.syncVideos();
      }, { signal });
      this.addEventListener("pointerleave", () => { this.hovered = false; this.scheduleResume(); this.syncVideos(); }, { signal });
      this.addEventListener("focusin", () => this.syncVideos(), { signal });
      this.addEventListener("focusout", () => queueMicrotask(() => { if (this.isConnected) { this.scheduleResume(); this.syncVideos(); } }), { signal });
      this.motion.addEventListener("change", () => { this.manual(); this.scheduleResume(); this.syncVideos(); }, { signal });
      document.addEventListener("visibilitychange", () => this.syncVideos(), { signal });
      window.addEventListener("load", () => { this.loaded = true; this.syncVideos(); }, { signal, once: true });
      this.sectionObserver = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.toggleAttribute("data-hf-offscreen", !this.visible);
        this.syncVideos();
      });
      this.sectionObserver.observe(this);
      this.resizeObserver = new ResizeObserver(() => this.buildLoop());
      this.resizeObserver.observe(this.viewport);
      this.buildLoop();
      if (this.motion.matches) this.manual();
    }

    duplicate(node) {
      const clone = node.cloneNode(true);
      clone.setAttribute("data-hf-marquee-duplicate", "");
      clone.setAttribute("aria-hidden", "true");
      [clone, ...clone.querySelectorAll("*")].forEach((el) => {
        ["id", "data-shopify-editor-block", "data-hf-marquee-original", "data-hf-marquee-block"].forEach((attr) => el.removeAttribute(attr));
        if (el.matches("a, video, button, [tabindex]")) el.setAttribute("tabindex", "-1");
        if (el.matches("video")) {
          el.replaceChildren();
          el.removeAttribute("data-hf-playing");
          el.removeAttribute("data-hf-failed");
        }
      });
      return clone;
    }

    buildLoop() {
      const oldPeriod = this.period;
      const phase = oldPeriod ? this.offset() % oldPeriod / oldPeriod : 0;
      this.captureVideoState();
      this.mediaController?.abort();
      this.videoObserver?.disconnect();
      this.querySelectorAll("video").forEach((video) => { if (!video.paused) video.pause(); });
      this.activeLogicalCards = new Map();
      this.querySelectorAll("[data-hf-marquee-duplicate]").forEach((el) => el.remove());
      const cards = Array.from(this.set.children);
      if (!cards.length) return;
      const gap = parseFloat(getComputedStyle(this.set).columnGap) || 0;
      this.baseWidth = cards.reduce((sum, card) => sum + card.getBoundingClientRect().width + gap, 0);
      if (!this.baseWidth) return;
      const copies = Math.max(1, Math.ceil(this.viewport.clientWidth / this.baseWidth));
      for (let i = 1; i < copies; i++) cards.forEach((card) => this.set.append(this.duplicate(card)));
      this.period = this.baseWidth * copies;
      // Three identical periods provide a buffer on both sides during native scrolling.
      this.track.append(this.duplicate(this.set), this.duplicate(this.set));
      // About 58 seconds for seven mobile cards; fewer cards don't speed up.
      this.style.setProperty("--hf-marquee-duration", `${(this.baseWidth * copies) / 30.65}s`);
      this.style.setProperty("--hf-marquee-distance", `${-this.period}px`);
      this.style.setProperty("--hf-marquee-delay", `${-phase * this.period / 30.65}s`);
      if (this.hasAttribute("data-hf-manual")) this.viewport.scrollLeft = this.period * (1 + phase);
      this.setAttribute("data-hf-ready", "");
      this.mediaController = new AbortController();
      this.videoRatios = new Map();
      this.videoKeys = new Map();
      this.pendingVideos = new Set();
      this.restoringVideos = new Map();
      const { signal } = this.mediaController;
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => this.videoRatios.set(entry.target, entry.intersectionRatio));
        this.syncVideos();
      }, { root: this.viewport, threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.6, 0.75, 0.9, 1] });
      this.querySelectorAll("video").forEach((video) => {
        this.videoKeys.set(video, video.closest("[data-marquee-card-id]")?.dataset.marqueeCardId);
        video.addEventListener("playing", () => {
          // A pending play may resolve after the selected card or interaction state changes.
          if (!this.isActiveVideo(video) || !this.videosAllowed()) {
            if (!video.paused) video.pause();
            return;
          }
          video.setAttribute("data-hf-playing", "");
        }, { signal });
        video.addEventListener("error", () => {
          video.removeAttribute("data-hf-playing");
          video.dataset.hfFailed = "true";
          this.syncVideos();
        }, { signal });
        this.videoObserver.observe(video);
      });
    }

    manual() {
      clearTimeout(this.resumeTimer);
      if (this.hasAttribute("data-hf-manual")) return;
      const offset = this.offset();
      this.setAttribute("data-hf-manual", "");
      this.viewport.scrollLeft = this.period ? this.period + offset % this.period : 0;
      this.syncVideos();
    }

    offset() {
      if (this.hasAttribute("data-hf-manual")) return this.viewport.scrollLeft;
      const transform = getComputedStyle(this.track).transform;
      return transform === "none" ? 0 : -new DOMMatrixReadOnly(transform).m41;
    }

    normalize() {
      if (!this.period || this.matches(":focus-within") && !this.pointer) return;
      const left = this.viewport.scrollLeft;
      if (left < this.period / 2 || left > this.period * 1.5) {
        this.captureVideoState();
        this.viewport.scrollLeft = this.period / 2 + (((left - this.period / 2) % this.period) + this.period) % this.period;
      }
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        if (!this.isConnected || !this.hasAttribute("data-hf-manual") || this.motion.matches || this.pointer || this.hovered || this.matches(":focus-within")) return;
        const phase = this.period ? this.viewport.scrollLeft % this.period : 0;
        this.style.setProperty("--hf-marquee-delay", `${-phase / 30.65}s`);
        this.viewport.scrollLeft = 0;
        this.removeAttribute("data-hf-manual");
        // The transform/scroll handoff changes which duplicate occupies the viewport.
        // Wait for a fresh observer batch instead of playing from stale visibility data.
        this.refreshVideoVisibility();
      }, 3000);
    }

    refreshVideoVisibility() {
      this.captureVideoState();
      this.activeLogicalCards.forEach(video => { if (!video.paused) video.pause(); });
      this.videoRatios?.clear();
      this.videoObserver?.disconnect();
      this.videoKeys?.forEach((key, video) => this.videoObserver.observe(video));
    }

    reveal(card) {
      const left = card.getBoundingClientRect().left - this.viewport.getBoundingClientRect().left + this.viewport.scrollLeft;
      this.viewport.scrollTo({ left: Math.max(0, left - 24), behavior: "auto" });
    }

    videosAllowed() {
      return this.isConnected && this.visible && this.loaded && !document.hidden && !this.motion.matches && !this.videoScrolling && !this.hasAttribute("data-hf-touch");
    }

    isActiveVideo(video) {
      return this.activeLogicalCards.get(this.videoKeys?.get(video)) === video;
    }

    captureVideoState(video) {
      if (!video) {
        this.activeLogicalCards?.forEach(owner => this.captureVideoState(owner));
        return;
      }
      const key = this.videoKeys?.get(video);
      // A destination still restoring is not yet the authoritative playhead.
      if (!key || !video.readyState || this.restoringVideos?.has(video)) return;
      if (Number.isFinite(video.currentTime)) this.logicalVideoStates.set(key, { currentTime: video.currentTime });
    }

    waitForMedia(video, event, signal) {
      return new Promise((resolve, reject) => {
        const finish = (error) => {
          clearTimeout(timer);
          video.removeEventListener(event, done);
          video.removeEventListener("error", cancelled);
          signal.removeEventListener("abort", cancelled);
          error ? reject(error) : resolve();
        };
        const done = () => finish();
        const cancelled = () => finish(new DOMException("Media handoff cancelled", "AbortError"));
        const timer = setTimeout(cancelled, 5000);
        video.addEventListener(event, done, { once: true });
        video.addEventListener("error", cancelled, { once: true });
        signal.addEventListener("abort", cancelled, { once: true });
        if (signal.aborted) cancelled();
      });
    }

    restoreVideoState(video) {
      if (this.restoringVideos.has(video)) return this.restoringVideos.get(video);
      const states = this.logicalVideoStates;
      const key = this.videoKeys.get(video);
      const restoring = this.restoringVideos;
      const { signal } = this.mediaController;
      const task = (async () => {
        if (!video.querySelector("source")) {
          const template = video.parentElement.querySelector("template");
          if (!template?.content.querySelector("source")) return;
          video.append(template.content.cloneNode(true));
          video.muted = true;
          video.load();
        }
        if (!states.has(key)) return; // Initial cards remain lazy; no clone-wide startup seeks.
        if (video.readyState < 1) await this.waitForMedia(video, "loadedmetadata", signal);
        if (signal.aborted) throw new DOMException("Stale handoff", "AbortError");
        const saved = states.get(key).currentTime;
        const duration = video.duration;
        if (!Number.isFinite(duration) || duration <= 0) return;
        const target = ((saved % duration) + duration) % duration;
        const difference = Math.abs(target - video.currentTime);
        if (Math.min(difference, Math.abs(duration - difference)) < 0.15) return;
        video.currentTime = target;
        if (video.seeking) await this.waitForMedia(video, "seeked", signal);
        // A paused incoming copy should show its restored frame, not its poster.
        if (!signal.aborted && !this.motion.matches) video.setAttribute("data-hf-playing", "");
      })().finally(() => { if (restoring.get(video) === task) restoring.delete(video); });
      restoring.set(video, task);
      return task;
    }

    syncVideos() {
      this.toggleAttribute("data-hf-offscreen", !this.visible || document.hidden);
      if (!this.videoRatios) return;
      const active = new Map();
      if (this.videosAllowed()) {
        const rail = this.viewport.getBoundingClientRect();
        const left = Math.max(0, rail.left), right = Math.min(innerWidth, rail.right);
        const top = Math.max(0, rail.top), bottom = Math.min(innerHeight, rail.bottom);
        const center = (left + right) / 2;
        const candidates = new Map();
        // Fresh geometry avoids ranking by stale observer ratios or DOM registration.
        // Observer thresholds/interactions trigger this; there is no frame polling.
        const compare = (a, b) => Math.abs(a.score - b.score) > 0.05
          ? b.score - a.score : a.distance - b.distance;
        this.videoKeys.forEach((key, video) => {
          if (!key || video.dataset.hfFailed) return;
          const rect = video.getBoundingClientRect();
          const area = rect.width * rect.height;
          const ratio = area ? Math.max(0, Math.min(right, rect.right) - Math.max(left, rect.left))
            * Math.max(0, Math.min(bottom, rect.bottom) - Math.max(top, rect.top)) / area : 0;
          const incumbent = this.activeLogicalCards.has(key);
          if (ratio < (incumbent ? 0.35 : 0.6)) return;
          const candidate = { key, video, ratio, distance: Math.abs((rect.left + rect.right) / 2 - center),
            score: ratio + (this.activeLogicalCards.get(key) === video ? 0.05 : 0) };
          if (!candidates.has(key) || compare(candidate, candidates.get(key)) < 0) candidates.set(key, candidate);
        });
        const ranked = Array.from(candidates.values()).map(candidate => ({ ...candidate,
          score: candidate.ratio + (this.activeLogicalCards.has(candidate.key) ? 0.1 : 0) }));
        ranked.sort(compare).slice(0, 2).forEach(({ key, video }) => active.set(key, video));
      }
      this.activeLogicalCards.forEach((video, key) => {
        if (active.get(key) !== video) this.captureVideoState(video);
      });
      this.activeLogicalCards = active;
      // Stop every outgoing owner before starting either destination.
      this.videoKeys.forEach((key, video) => {
        if (!this.isActiveVideo(video) && !video.paused) video.pause();
      });
      this.videoRatios.forEach((ratio, video) => {
        if (!this.isActiveVideo(video)) {
          if (this.motion.matches) video.removeAttribute("data-hf-playing");
          // Only visible copies with saved state need a paused-frame handoff.
          else if (ratio > 0 && this.logicalVideoStates.has(this.videoKeys.get(video))) {
            this.restoreVideoState(video).catch(() => {});
          }
          return;
        }
        if (!video.paused || this.pendingVideos.has(video)) return;
        this.pendingVideos.add(video);
        const pending = this.pendingVideos;
        const { signal } = this.mediaController;
        this.restoreVideoState(video).then(() => {
          if (!signal.aborted && this.isActiveVideo(video) && this.videosAllowed()) return video.play();
        }).catch((error) => {
          // Aborting a pending play during pause/teardown is not a broken media file.
          if (signal.aborted || this.pendingVideos !== pending || error.name === "AbortError") return;
          video.dataset.hfFailed = "true";
          video.removeAttribute("data-hf-playing");
        }).finally(() => {
          pending.delete(video);
          if (!this.isConnected || this.pendingVideos !== pending) return;
          if (!this.isActiveVideo(video) || !this.videosAllowed()) { if (!video.paused) video.pause(); }
        });
      });
    }

    disconnectedCallback() {
      this.captureVideoState();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.videoSettleTimer);
      clearTimeout(this.clickTimer);
      this.controller?.abort();
      this.mediaController?.abort();
      this.sectionObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.querySelectorAll("video").forEach((video) => {
        if (!video.paused) video.pause();
        video.replaceChildren(); video.load();
        video.removeAttribute("data-hf-playing");
      });
      this.querySelectorAll("[data-hf-marquee-duplicate]").forEach((el) => el.remove());
      this.removeAttribute("data-hf-ready");
      ["data-hf-manual", "data-hf-dragging", "data-hf-touch"].forEach((attr) => this.removeAttribute(attr));
      this.pointer = null;
      this.period = 0;
      if (this.viewport) this.viewport.scrollLeft = 0;
      this.style.removeProperty("--hf-marquee-delay");
      this.controller = null;
    }
  });
}

// Image heroes have no JavaScript hooks. Only video heroes instantiate this element.
if (!customElements.get("hf-hero-video")) {
  customElements.define("hf-hero-video", class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.video = this.querySelector("video");
      if (!this.video) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.mobile = matchMedia("(max-width: 1023px)");
      this.motion = matchMedia("(prefers-reduced-motion: reduce)");
      this.visible = false;
      this.failed = false;
      this.sourceKey = null;
      this.video.muted = true;
      this.video.addEventListener("playing", () => {
        this.setAttribute("data-hf-has-frame", "");
      }, { signal });
      this.video.addEventListener("error", () => {
        this.failed = true;
        this.removeAttribute("data-hf-has-frame");
      }, { signal });
      this.mobile.addEventListener("change", () => {
        this.video.pause();
        this.video.replaceChildren();
        this.video.load();
        this.sourceKey = null;
        this.failed = false;
        this.video.muted = true;
        this.removeAttribute("data-hf-has-frame");
        this.syncPlayback();
      }, { signal });
      this.motion.addEventListener("change", () => {
        if (this.motion.matches) {
          this.video.pause();
          this.removeAttribute("data-hf-has-frame");
        } else this.syncPlayback();
      }, { signal });
      document.addEventListener("visibilitychange", () => this.syncPlayback(), { signal });
      this.observer = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.syncPlayback();
      }, { threshold: 0 });
      this.observer.observe(this);
    }

    async playVideo() {
      if (this.failed || !this.isConnected) return;
      const key = this.mobile.matches ? "mobile" : "desktop";
      if (this.sourceKey !== key) {
        const template = this.querySelector(`[data-hf-hero-${key}]`);
        if (!template?.content.querySelector("source")) return;
        this.video.replaceChildren(template.content.cloneNode(true));
        this.sourceKey = key;
        this.video.load();
      }
      try {
        await this.video.play();
      } catch {}
    }

    syncPlayback() {
      if (!this.visible || document.hidden) {
        this.video.pause();
        this.video.muted = true;
      } else if (!this.motion.matches) {
        this.playVideo();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      if (this.video) {
        this.video.pause();
        this.video.replaceChildren();
        this.video.load();
      }
      this.removeAttribute("data-hf-has-frame");
      this.controller = null;
    }
  });
}

(() => {
  const instances = new Map();
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function replaceFromState(card, state, sourceSelector, targetSelector) {
    const source = state.querySelector(sourceSelector);
    const target = card.querySelector(targetSelector);
    if (!source || !target) return;

    target.replaceChildren(...Array.from(source.childNodes, (node) => node.cloneNode(true)));
  }

  function getSiblingSwatchOrder(item) {
    const rawOrder = item.dataset.hfSwatchOrder;
    if (rawOrder === undefined) return Number.POSITIVE_INFINITY;

    const order = Number(rawOrder);
    return Number.isInteger(order) ? order : Number.POSITIVE_INFINITY;
  }

  function sortSiblingSwatches(root) {
    root.querySelectorAll("[data-hf-sibling-swatches]").forEach((group) => {
      Array.from(group.children)
        .filter((item) => item.matches("[data-hf-swatch-item]"))
        .map((item, index) => ({ item, index, order: getSiblingSwatchOrder(item) }))
        .sort((left, right) => (left.order === right.order ? left.index - right.index : left.order - right.order))
        .forEach(({ item }) => group.append(item));
    });
  }

  function selectSiblingProduct(input) {
    const card = input.closest("[data-hf-product-card]");
    const template = input.parentElement?.querySelector("template[data-hf-sibling-state]");
    if (!card || !(template instanceof HTMLTemplateElement)) return;

    const state = template.content;
    const productUrl = input.dataset.productUrl;
    const productTitle = input.dataset.productTitle;
    const swatchGroup = input.closest("[data-hf-sibling-swatches]");

    swatchGroup?.querySelectorAll("[data-hf-sibling-swatch]").forEach((swatch) => {
      const isCurrent = swatch === input;
      const label = swatch.labels?.[0];

      if (isCurrent) {
        swatch.setAttribute("aria-current", "true");
      } else {
        swatch.removeAttribute("aria-current");
      }

      label?.classList.toggle("is-active", isCurrent);
    });

    if (productUrl) {
      card.querySelectorAll("[data-hf-product-link]").forEach((link) => {
        link.href = productUrl;
      });
    }

    const title = card.querySelector("[data-hf-card-title]");
    if (title && productTitle) title.textContent = productTitle;

    const imageLink = card.querySelector(".hf-product-card__image-link");
    if (imageLink && productTitle) imageLink.setAttribute("aria-label", productTitle);

    replaceFromState(card, state, "[data-hf-state-media]", "[data-hf-card-media]");
    replaceFromState(card, state, "[data-hf-state-category]", "[data-hf-card-category]");
    replaceFromState(card, state, "[data-hf-state-description]", "[data-hf-card-description]");
    replaceFromState(card, state, "[data-hf-state-rating]", "[data-hf-card-rating]");
    replaceFromState(card, state, "[data-hf-state-price]", "[data-hf-card-price]");
    replaceFromState(card, state, "[data-hf-state-badge]", ".hf-product-card__badge-wrap");
    replaceFromState(card, state, "[data-hf-state-action]", "[data-hf-card-action]");

    if (input.dataset.productId) card.dataset.hfProductId = input.dataset.productId;
  }

  function initSection(root) {
    if (!(root instanceof HTMLElement) || instances.has(root)) return;

    const carousel = root.querySelector("[data-hf-carousel]");
    const productCards = root.querySelectorAll("[data-hf-product-card]");
    if (!carousel && productCards.length === 0) return;

    const controller = new AbortController();
    const { signal } = controller;

    sortSiblingSwatches(root);

    if (carousel) {
      carousel.addEventListener(
        "keydown",
        (event) => {
          if (event.target !== carousel) return;
          if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

          const item = carousel.querySelector("[data-hf-carousel-item]");
          if (!item) return;

          event.preventDefault();
          const gap = Number.parseFloat(getComputedStyle(carousel).columnGap) || 0;
          const direction = event.key === "ArrowRight" ? 1 : -1;

          carousel.scrollBy({
            left: direction * (item.getBoundingClientRect().width + gap),
            behavior: reduceMotion.matches ? "auto" : "smooth",
          });
        },
        { signal },
      );
    }

    root.addEventListener(
      "change",
      (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || !input.matches("[data-hf-sibling-swatch]")) return;
        selectSiblingProduct(input);
      },
      { signal },
    );

    root.dataset.hfInitialized = "true";
    instances.set(root, controller);
  }

  function destroySection(root) {
    const controller = instances.get(root);
    if (!controller) return;

    controller.abort();
    instances.delete(root);
    delete root.dataset.hfInitialized;
  }

  function initWithin(scope = document) {
    if (scope.matches?.("[data-hf-section]")) initSection(scope);
    scope.querySelectorAll?.("[data-hf-section]").forEach(initSection);
  }

  initWithin();

  document.addEventListener("shopify:section:load", (event) => {
    initWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    if (event.target.matches?.("[data-hf-section]")) destroySection(event.target);
    event.target.querySelectorAll?.("[data-hf-section]").forEach(destroySection);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const item = event.target.closest?.("[data-hf-carousel-item]");
    if (!item) return;

    item.scrollIntoView({
      behavior: reduceMotion.matches ? "auto" : "smooth",
      block: "nearest",
      inline: "center",
    });
  });
})();
